import type { AgeGroup, Lesson } from '@court-sync/shared';
import { computeCampWeekSessionDates } from '@court-sync/shared';
import { format } from 'date-fns';

export function campHasEnrollments(camp: Lesson): boolean {
  return (camp.currentEnrollment ?? 0) > 0;
}

export function parseCampDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const vv = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (vv.toDate) return vv.toDate();
  if (typeof vv.seconds === 'number') return new Date(vv.seconds * 1000);
  if (typeof vv._seconds === 'number') return new Date(vv._seconds * 1000);
  return null;
}

export function formatDateRange(startDate: unknown, endDate: unknown): string {
  const fmt = (v: unknown): string => {
    if (!v) return '';
    if (v instanceof Date) return v.toLocaleDateString();
    if (typeof v === 'string') return new Date(v).toLocaleDateString();
    const vv = v as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (vv.toDate) return vv.toDate().toLocaleDateString();
    if (vv.seconds) return new Date(vv.seconds * 1000).toLocaleDateString();
    if (vv._seconds) return new Date(vv._seconds * 1000).toLocaleDateString();
    return '';
  };
  const s = fmt(startDate);
  const e = fmt(endDate);
  if (s && e) return `${s} – ${e}`;
  if (s) return `From ${s}`;
  if (e) return `Until ${e}`;
  return '—';
}

/** Camp has ended (end date before today, local calendar day). */
export function isCampPast(camp: Lesson): boolean {
  const end = parseCampDate((camp as { endDate?: unknown }).endDate);
  if (!end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  return endDay < today;
}

export function getInstancesDateSpan(instances: Lesson[]): string {
  if (instances.length === 0) return '—';
  let minStart: Date | null = null;
  let maxEnd: Date | null = null;
  for (const instance of instances) {
    const start = parseCampDate((instance as { startDate?: unknown }).startDate);
    const end = parseCampDate((instance as { endDate?: unknown }).endDate);
    if (start && (!minStart || start < minStart)) minStart = start;
    if (end && (!maxEnd || end > maxEnd)) maxEnd = end;
  }
  if (!minStart && !maxEnd) return '—';
  return formatDateRange(minStart, maxEnd);
}

/** Program, custom age group, or legacy juniors/adults label for a camp instance row. */
export function getCampInstanceTrackLabel(
  lesson: Lesson,
  options: {
    ageGroups: AgeGroup[];
    programs: Array<{ id: string; name: string }>;
  }
): string | null {
  const parts: string[] = [];

  if (lesson.programId) {
    const program = options.programs.find((p) => p.id === lesson.programId);
    if (program?.name) parts.push(program.name);
  }

  if (lesson.ageGroupId) {
    const group = options.ageGroups.find((g) => g.id === lesson.ageGroupId);
    if (group?.label) parts.push(group.label);
  } else if (lesson.ageGroup && lesson.ageGroup !== 'all') {
    parts.push(lesson.ageGroup.charAt(0).toUpperCase() + lesson.ageGroup.slice(1));
  }

  if (parts.length === 0) return null;
  return parts.join(' · ');
}

/** Camp group API instances omit enrollment; merge from the lessons store after refresh. */
export function mergeLessonEnrollmentFromStore(
  instance: Lesson,
  lessonsById: Map<string, Lesson>
): Lesson {
  const fresh = lessonsById.get(instance.id);
  if (!fresh) return instance;
  return {
    ...instance,
    currentEnrollment: fresh.currentEnrollment,
    pendingEnrollment: fresh.pendingEnrollment,
  };
}

function campDateToYmd(value: unknown): string | null {
  const d = parseCampDate(value);
  if (!d) return null;
  return format(d, 'yyyy-MM-dd');
}

/** Camp days available for staff enroll (explicit sessions, stored dates, or computed week). */
export async function resolveCampSessionDatesForEnroll(
  camp: Lesson,
  fetchSessions: (lessonId: string) => Promise<Array<{ sessionDate: string }>>
): Promise<string[]> {
  if (camp.campSessionDates?.length) {
    return [...camp.campSessionDates].filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort();
  }
  if (camp.campSessionsExplicit) {
    const sessions = await fetchSessions(camp.id);
    return sessions
      .map((s) => String(s.sessionDate).replace(/T.*/, '').slice(0, 10))
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
      .sort();
  }
  const startYmd = campDateToYmd((camp as { startDate?: unknown }).startDate);
  const daysOfWeek = camp.daysOfWeek ?? [];
  if (!startYmd || daysOfWeek.length === 0) return [];
  return computeCampWeekSessionDates(startYmd, daysOfWeek);
}
